import { useState } from 'react';

import { Button } from '@frontend/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@frontend/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@frontend/components/ui/sidebar';
import { Skeleton } from '@frontend/components/ui/skeleton';
import { useCurrentUser } from '@frontend/features/admin/hooks/useCurrentUser';
import { useConversations } from '@frontend/features/chat/hooks/useConversations';
import { CreateFolderDialog } from '@frontend/features/documents/components/CreateFolderDialog';
import { ShareFolderDialog } from '@frontend/features/documents/components/ShareFolderDialog';
import { useFolders } from '@frontend/features/documents/hooks/useFolders';
import type { Folder } from '@frontend/features/documents/types';
import { Link, useLocation, useParams } from '@tanstack/react-router';
import {
  ClipboardList,
  FolderLock,
  FolderOpen,
  MessageSquarePlus,
  MoreHorizontal,
  Plus,
  Shield,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';

export function AppSidebar() {
  const location = useLocation();
  const params = useParams({ strict: false }) as { conversationId?: string };
  const { toggleSidebar } = useSidebar();

  // Current user
  const { user, isAdmin } = useCurrentUser();

  // Conversations
  const { conversations, isLoading: conversationsLoading } = useConversations();

  // Folders
  const {
    folders,
    isLoading: foldersLoading,
    createFolder,
    isCreating,
    updateFolder,
    isUpdating,
    deleteFolder,
  } = useFolders(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [shareFolderTarget, setShareFolderTarget] = useState<Folder | null>(
    null,
  );

  const activeConversationId = params.conversationId;
  const activeFolderId = (params as Record<string, string | undefined>)
    .folderId;

  const handleCreateFolder = async (
    name: string,
    visibility: 'public' | 'private',
  ) => {
    await createFolder(name, visibility);
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-between h-14 px-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
            </div>
            <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
              EventsDC
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Conversations */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
            Conversations
          </SidebarGroupLabel>
          <SidebarGroupAction asChild>
            <Link to="/chat">
              <MessageSquarePlus className="h-3.5 w-3.5" />
              <span className="sr-only">New Chat</span>
            </Link>
          </SidebarGroupAction>
          <SidebarGroupContent>
            <SidebarMenu>
              {conversationsLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <SidebarMenuItem key={i}>
                    <Skeleton className="h-7 w-full rounded-md" />
                  </SidebarMenuItem>
                ))}

              {conversations.map((conv) => {
                const isActive = activeConversationId === conv.id;
                return (
                  <SidebarMenuItem key={conv.id}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link
                        to="/chat/$conversationId"
                        params={{ conversationId: conv.id }}
                      >
                        <span className="truncate text-[13px]">
                          {conv.title || 'New conversation'}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="mx-4 opacity-60" />

        {/* Documents / Folders */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
            Folders
          </SidebarGroupLabel>
          <SidebarGroupAction onClick={() => setCreateFolderOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            <span className="sr-only">New Folder</span>
          </SidebarGroupAction>
          <SidebarGroupContent>
            <SidebarMenu>
              {foldersLoading &&
                Array.from({ length: 2 }).map((_, i) => (
                  <SidebarMenuItem key={i}>
                    <Skeleton className="h-7 w-full rounded-md" />
                  </SidebarMenuItem>
                ))}

              {folders.map((folder) => {
                const isActive = activeFolderId === folder.id;
                const isPublic = folder.visibility === 'public';
                const isOwner = folder.createdBy === user?.userId;
                const FolderIcon = isPublic ? FolderOpen : FolderLock;

                return (
                  <SidebarMenuItem key={folder.id}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link
                        to="/folder/$folderId"
                        params={{ folderId: folder.id }}
                      >
                        <FolderIcon className="h-3.5 w-3.5" />
                        <span className="truncate text-[13px]">
                          {folder.name}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                    {isOwner && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuAction>
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </SidebarMenuAction>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start">
                          <DropdownMenuItem
                            onClick={() => setShareFolderTarget(folder)}
                          >
                            <Users className="h-3.5 w-3.5 mr-2" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => deleteFolder(folder.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {isAdmin && (
          <>
            <SidebarSeparator className="mx-4 opacity-60" />
            <SidebarGroup>
              <SidebarGroupLabel className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                Admin
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === '/admin'}
                    >
                      <Link to="/admin">
                        <Shield className="h-3.5 w-3.5" />
                        <span className="text-[13px]">User Management</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.startsWith(
                        '/admin/audit-logs',
                      )}
                    >
                      <Link to="/admin/audit-logs">
                        <ClipboardList className="h-3.5 w-3.5" />
                        <span className="text-[13px]">Audit Log</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>

      {/* Create folder dialog */}
      <CreateFolderDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        onCreate={handleCreateFolder}
        isCreating={isCreating}
      />

      {/* Share folder dialog */}
      {shareFolderTarget && (
        <ShareFolderDialog
          open={!!shareFolderTarget}
          onOpenChange={(open) => {
            if (!open) setShareFolderTarget(null);
          }}
          folder={shareFolderTarget}
          onSave={(sharedWith) => {
            updateFolder({ id: shareFolderTarget.id, sharedWith });
            setShareFolderTarget(null);
          }}
          isSaving={isUpdating}
        />
      )}
    </Sidebar>
  );
}
